import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BackupStatisticGrid } from '@common/components/delete-user-backup/delete-user-backup.model';
import { ServiceTypeMetadataMap } from '@common/constants';
import { ServiceType } from '@common/enums';
import { BackupOptions, BackupStatistic, DeleteBackupPayload, OdataObject } from '@common/models';
import { BackupSharedDrivesTypePayload, BTeamDrivesResponse } from '@common/models/team-drives';
import { ODataServiceFactory } from '@common/odata';
import { AbilityService, AuthContextService } from '@common/services/auth';
import { getErrorText, getServiceUiInfo, hasErrorResponseText } from '@common/utils';
import { BackupPayload } from '@pages/users/user-sidepanel/tabs/backup-tab/backup-tab.model';
import { I18NextPipe } from 'angular-i18next';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BackupService {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly http = inject(HttpClient);
  private readonly i18n = inject(I18NextPipe);
  private readonly odataFactory = inject(ODataServiceFactory);

  private readonly backupUsers = this.odataFactory.CreateService<any>('BackupUsers');
  private readonly odataMethods = this.odataFactory.CreateService('');

  protected readonly authDomainId = this.authContext.domainId;
  protected readonly isUserAccountAdmin = this.ability.isUserAccountAdmin;

  canBackup(userId: string, serviceType: ServiceType): Observable<null | string> {
    const serviceName = ServiceType[serviceType];

    return this.backupUsers.ItemProperty(userId, `BackupStatus?service=${serviceName}`).pipe(
      map((res: OdataObject<string>) => res?.value),
      catchError((res: HttpErrorResponse) => of(hasErrorResponseText(res) ? getErrorText(res) : 'Sorry, something went wrong'))
    );
  }

  startBackup(userId: string, serviceType: ServiceType): Observable<any> {
    return this.backupUsers.CustomAction(userId, 'Backup', { ServiceType: ServiceType[serviceType] });
  }

  deleteBackup(payload: DeleteBackupPayload): Observable<void> {
    return this.odataMethods.CustomCollectionAction('DeleteBackup', payload);
  }

  bulkBackup(payload: BackupPayload): Observable<void> {
    return this.odataMethods.CustomCollectionAction('BulkBackup', payload);
  }

  getBackupOptions(userId: string): Observable<BackupOptions> {
    return this.backupUsers
      .CustomFunction(userId, 'BackupOptions')
      .pipe(map((res: HttpResponse<BackupOptions>) => this.prepareBackupOptions(res.body)));
  }

  setBackupOptions(userId: string, options: BackupOptions): Observable<any> {
    return this.backupUsers.CustomAction(userId, 'SetBackupOptions', options);
  }

  getArchiveOptions(userId: string): Observable<BackupOptions> {
    return this.backupUsers
      .CustomFunction(userId, 'ArchiveOptions')
      .pipe(map((res: HttpResponse<BackupOptions>) => this.prepareBackupOptions(res.body)));
  }

  setArchiveOptions(userId: string, options: BackupOptions): Observable<any> {
    return this.backupUsers.CustomAction(userId, 'SetArchiveOptions', options);
  }

  getBackupStatistic(userIds: string[], include: boolean): Observable<BackupStatistic[]> {
    const odataService = this.odataFactory.CreateService(
      `GetBackupStatistic?userIds=${userIds.join(',')}&includeTeamDriveSharePoint=${include}`
    );

    return this.http.get<OdataObject<BackupStatistic[]>>(odataService.Query().GetUrl()).pipe(map((res) => res.value));
  }

  mapVisibleBackupStatsToGrid(stats: BackupStatistic[], isGlobalAdmin = true): BackupStatisticGrid[] {
    const getServiceType = (type: string): ServiceType => ServiceTypeMetadataMap[type].serviceType;
    const hasBasicAccess = !this.isUserAccountAdmin() && isGlobalAdmin;

    return stats.reduce((result, stat) => {
      const serviceType = getServiceType(stat.Type);

      if (!hasBasicAccess && (serviceType === ServiceType.SharePoint || serviceType === ServiceType.TeamDrives)) {
        return result;
      }

      result.push({
        name: this.i18n.transform(getServiceUiInfo(serviceType).i18nKey, { format: 'capitalize' }),
        size: stat.Size,
        count: serviceType === ServiceType.Contacts ? stat.ItemCount || 0 : stat.ItemCount,
        serviceType
      });

      return result;
    }, []);
  }

  private prepareBackupOptions(options: BackupOptions): BackupOptions {
    if (options) {
      delete options['@odata.context'];
      options.ApplyToAllDomainUsers = false;
    }

    return options;
  }

  getBackupSharedDrivesType(): Observable<BTeamDrivesResponse> {
    const domainId = this.authDomainId();
    const odataService = this.odataFactory.CreateService<BTeamDrivesResponse>(`/Domains(${domainId})/GetBackupSharedDrivesType`);

    return this.http.get<BTeamDrivesResponse>(odataService.Query().GetUrl());
  }

  updateBackupSharedDrivesType(payload: BackupSharedDrivesTypePayload): Observable<void> {
    const domainId = this.authDomainId();
    const odataService = this.odataFactory.CreateService<BTeamDrivesResponse>(`/Domains(${domainId})/ChangeBackupSharedDrivesType`);

    return this.http.post<void>(odataService.Query().GetUrl(), payload);
  }
}
